import { useState } from 'react'
import { useModal } from './useModal'

type BaseModalWithSelectedItemProps<TSelectedItem> = {
  toggle: () => void
  openWith: (item: TSelectedItem) => void
  close: () => void
}

export type ClosedModalWithSelectedItemProps<TSelectedItem> =
  BaseModalWithSelectedItemProps<TSelectedItem> & {
    type: 'closed'
    isOpen: false
    selectedItem: null
  }

export type OpenedModalWithSelectedItemProps<TSelectedItem> =
  BaseModalWithSelectedItemProps<TSelectedItem> & {
    type: 'opened'
    isOpen: true
    selectedItem: TSelectedItem
  }

export type ModalWithSelectedItemProps<TSelectedItem> =
  | ClosedModalWithSelectedItemProps<TSelectedItem>
  | OpenedModalWithSelectedItemProps<TSelectedItem>

/**
 * This type-safe hook is used to power modals, in the case where the modal should
 * only be open when a selected item is present.
 */
export function useModalWithSelectedItem<
  TSelectedItem
>(): ModalWithSelectedItemProps<TSelectedItem> {
  const { isOpen, open: openInternal, close: closeInternal } = useModal()
  const [selectedItem, setSelectedItem] = useState<TSelectedItem | null>(null)

  const openWith = (item: TSelectedItem) => {
    setSelectedItem(item)
    openInternal()
  }

  const close = () => {
    setSelectedItem(null)
    closeInternal()
  }

  const toggle = () => {
    if (isOpen) {
      close()
    } else {
      openInternal()
    }
  }

  const baseProps: BaseModalWithSelectedItemProps<TSelectedItem> = {
    toggle,
    openWith,
    close,
  }

  if (isOpen) {
    return {
      ...baseProps,
      type: 'opened',
      isOpen: true,
      selectedItem: selectedItem!,
    }
  }

  return {
    ...baseProps,
    type: 'closed',
    isOpen: false,
    selectedItem: null,
  }
}
